import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { X, Gift, Check, Sparkles, Crown, Zap } from 'lucide-react';
import { GemIcon } from './GemIcon';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DAILY_REWARDS = [25, 40, 40, 75, 60, 60, 100];

// Day bonuses (1-indexed)
const DAY_BONUSES = {
  4: { type: 'premium', label: '+15 Premium', icon: Crown, color: '#F59E0B' },
  7: { type: 'ultimate', label: '+10 Ultimate', icon: Zap, color: '#E11D48' },
};

export default function DailyRewards({ isOpen, onClose }) {
  const { user } = useAuth();
  const [gemsData, setGemsData] = useState(null);
  const [claiming, setClaiming] = useState(false);
  const [claimResult, setClaimResult] = useState(null);

  useEffect(() => {
    if (isOpen && user) {
      fetchGems();
      setClaimResult(null);
    }
  }, [isOpen, user]);

  const fetchGems = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/user/gems`, { withCredentials: true });
      setGemsData(data);
    } catch (err) {
      console.error('Error fetching gems:', err);
    }
  };

  const claimReward = async () => {
    setClaiming(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/user/gems/claim`, {}, { withCredentials: true });
      setClaimResult(data);
      await fetchGems();
    } catch (err) {
      console.error('Error claiming:', err);
    } finally {
      setClaiming(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
      <div className="w-full max-w-sm bg-[#0F1115] rounded-2xl border border-white/10 overflow-hidden">
        {/* Header */}
        <div className="relative p-4 bg-gradient-to-br from-[#E11D48]/20 to-[#F59E0B]/20">
          <button
            onClick={onClose}
            className="absolute top-3 right-3 p-1 text-[#A1A1AA] hover:text-white"
            data-testid="close-rewards"
          >
            <X className="w-5 h-5" />
          </button>
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 mb-3 bg-gradient-to-br from-[#E11D48] to-[#F59E0B] rounded-2xl">
              <Gift className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-xl font-bold text-white font-['Unbounded']">Daily Rewards</h2>
            <p className="text-sm text-[#A1A1AA] mt-1">Log in daily to earn gems!</p>
          </div>
        </div>

        {/* Rewards Grid */}
        <div className="p-4">
          <div className="grid grid-cols-7 gap-1.5 mb-4">
            {DAILY_REWARDS.map((gems, index) => {
              const dayNum = index + 1;
              const isCompleted = gemsData && index < gemsData.streak;
              const isCurrent = gemsData && index === gemsData.streak && gemsData.can_claim;
              const bonus = DAY_BONUSES[dayNum];
              
              return (
                <div
                  key={index}
                  className={`relative flex flex-col items-center justify-center p-1.5 rounded-lg border transition-all ${
                    isCompleted
                      ? 'bg-[#F59E0B]/20 border-[#F59E0B]/50'
                      : isCurrent
                      ? 'bg-[#E11D48]/20 border-[#E11D48] animate-pulse'
                      : bonus
                      ? `bg-[${bonus.color}]/5 border-[${bonus.color}]/20`
                      : 'bg-[#1A1D24] border-white/5'
                  }`}
                  data-testid={`reward-day-${dayNum}`}
                >
                  <span className="text-[9px] text-[#71717A] mb-0.5">Day {dayNum}</span>
                  <GemIcon size={16} />
                  <span className={`text-[11px] font-bold ${isCompleted ? 'text-[#F59E0B]' : isCurrent ? 'text-[#E11D48]' : 'text-[#A1A1AA]'}`}>
                    {gems}
                  </span>
                  {/* Bonus badge */}
                  {bonus && (
                    <div
                      className="mt-0.5 px-1 py-0.5 rounded text-[7px] font-bold leading-none whitespace-nowrap"
                      style={{ backgroundColor: `${bonus.color}20`, color: bonus.color }}
                    >
                      {bonus.label}
                    </div>
                  )}
                  {isCompleted && (
                    <div className="absolute -top-1 -right-1 w-4 h-4 bg-[#F59E0B] rounded-full flex items-center justify-center">
                      <Check className="w-3 h-3 text-white" />
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Claim Button or Status */}
          {claimResult ? (
            <div className="text-center p-4 bg-gradient-to-br from-[#E11D48]/20 to-[#F59E0B]/20 rounded-xl space-y-2">
              <Sparkles className="w-8 h-8 text-[#F59E0B] mx-auto" />
              <p className="text-lg font-bold text-white">+{claimResult.reward} Gems!</p>
              {claimResult.bonus_type === 'premium' && (
                <div className="flex items-center justify-center gap-1.5 px-3 py-1.5 bg-[#F59E0B]/20 border border-[#F59E0B]/30 rounded-full mx-auto w-fit">
                  <Crown className="w-4 h-4 text-[#F59E0B]" />
                  <span className="text-xs font-bold text-[#F59E0B]">+15 Premium Messages Unlocked!</span>
                </div>
              )}
              {claimResult.bonus_type === 'ultimate' && (
                <div className="flex items-center justify-center gap-1.5 px-3 py-1.5 bg-[#E11D48]/20 border border-[#E11D48]/30 rounded-full mx-auto w-fit">
                  <Zap className="w-4 h-4 text-[#E11D48]" />
                  <span className="text-xs font-bold text-[#E11D48]">+10 Ultimate Messages Unlocked!</span>
                </div>
              )}
              <p className="text-sm text-[#A1A1AA]">Come back tomorrow for more</p>
            </div>
          ) : gemsData?.can_claim ? (
            <div className="space-y-2">
              <Button
                onClick={claimReward}
                disabled={claiming}
                className="w-full bg-gradient-to-r from-[#E11D48] to-[#F59E0B] hover:opacity-90 text-white rounded-xl py-3"
                data-testid="claim-reward-button"
              >
                {claiming ? (
                  <span className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Claiming...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <Gift className="w-5 h-5" />
                    Claim Day {gemsData.next_day} Rewards
                  </span>
                )}
              </Button>
              {/* Preview of today's bonus */}
              {gemsData.next_day === 4 && (
                <div className="flex items-center justify-center gap-1.5 p-2 bg-[#F59E0B]/10 border border-[#F59E0B]/20 rounded-lg">
                  <Crown className="w-3.5 h-3.5 text-[#F59E0B]" />
                  <span className="text-xs text-[#F59E0B] font-medium">Today: {gemsData.next_reward} gems + 15 Premium messages!</span>
                </div>
              )}
              {gemsData.next_day === 7 && (
                <div className="flex items-center justify-center gap-1.5 p-2 bg-[#E11D48]/10 border border-[#E11D48]/20 rounded-lg">
                  <Zap className="w-3.5 h-3.5 text-[#E11D48]" />
                  <span className="text-xs text-[#E11D48] font-medium">Today: {gemsData.next_reward} gems + 10 Ultimate messages!</span>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center p-4 bg-[#1A1D24] rounded-xl">
              <p className="text-[#A1A1AA]">Already claimed today!</p>
              <p className="text-xs text-[#71717A] mt-1">Come back tomorrow</p>
            </div>
          )}

          {/* Current Gems */}
          <div className="mt-4 flex items-center justify-center gap-2 p-3 bg-[#1A1D24] rounded-xl">
            <GemIcon size={20} />
            <span className="text-white font-bold">{(gemsData?.gems || 0).toLocaleString()}</span>
            <span className="text-[#71717A]">Total Gems</span>
          </div>
        </div>
      </div>
    </div>
  );
}
